import { faCircleInfo, faGift } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { twMerge } from 'tailwind-merge'
import { useBalance } from '../api'

const coinIcon = new URL('../assets/coin.png', import.meta.url).toString()

/**
 * One vocabulary for the two kinds of money, used everywhere either is shown.
 *
 * They are not interchangeable: `useCheckBalance` in the store settles purchases against `usdc`
 * alone, so bonus funds cannot buy anything there. Presenting a single `usdc + airdrop` total —
 * which is what the menu widget did — quotes users a number they cannot spend in the store.
 *
 * Spendable money is white on the coin mark. Bonus money is amber with a gift mark and never
 * borrows the coin, so the restriction is legible before anyone reads a label.
 */

export const SpendableFunds = ({ amount, className }: { amount?: number; className?: string }) => (
    <span className={twMerge('inline-flex flex-row items-center gap-1.5 font-bold whitespace-nowrap', className)}>
        <img src={coinIcon} alt='' className='h-[1.2em] w-auto' />
        {(amount ?? 0).toFixed(2)}
    </span>
)

export const BonusFunds = ({
    amount,
    className,
    showLabel = true,
}: {
    amount?: number
    className?: string
    showLabel?: boolean
}) => (
    <span
        title='Bonus funds: spawn fees only, not valid for store purchases'
        className={twMerge(
            'inline-flex flex-row items-center gap-1.5 font-bold whitespace-nowrap text-amber-400',
            className
        )}
    >
        <FontAwesomeIcon icon={faGift} className='text-[0.9em]' />
        {(amount ?? 0).toFixed(2)}
        {showLabel && <span className='text-[0.75em] uppercase tracking-wide opacity-80'>Bonus</span>}
    </span>
)

/** The sentence that has to appear anywhere someone might try to spend the wrong balance. */
export const BonusFundsNote = ({ className }: { className?: string }) => (
    <div className={twMerge('flex flex-row items-start gap-2 text-xs text-white/60', className)}>
        <FontAwesomeIcon icon={faCircleInfo} className='text-amber-400/80 mt-0.5 shrink-0' />
        <span>
            <span className='text-amber-400 font-bold'>Bonus funds</span> cover in-game spawn fees. Store purchases use
            your spendable balance.
        </span>
    </div>
)

/**
 * Both balances side by side. `hideEmptyBonus` keeps the row from advertising a bonus of zero to
 * users who have never had one.
 */
export const FundsSummary = ({
    className,
    hideEmptyBonus = true,
}: {
    className?: string
    hideEmptyBonus?: boolean
}) => {
    const { data: balance, isFetched } = useBalance()
    if (!isFetched) return null

    const hasBonus = (balance?.airdrop ?? 0) > 0

    return (
        <div className={twMerge('flex flex-row items-center gap-3 flex-wrap', className)}>
            <span className='flex flex-row items-baseline gap-2'>
                <SpendableFunds amount={balance?.usdc} />
                <span className='text-[0.7em] uppercase tracking-wide text-white/50'>Spendable</span>
            </span>
            {(hasBonus || !hideEmptyBonus) && (
                <>
                    <span className='text-white/20'>|</span>
                    <BonusFunds amount={balance?.airdrop} />
                </>
            )}
        </div>
    )
}

export const Funds = {
    Spendable: SpendableFunds,
    Bonus: BonusFunds,
    Note: BonusFundsNote,
    Summary: FundsSummary,
}
